import { differenceInCalendarDays, format, isToday, isYesterday } from 'date-fns';
import type { TFunction } from 'i18next';

import { dfLocale } from '@/lib/date-fns-locale';

/**
 * Friendly day-divider labels shared by the message thread and the notes
 * timeline so both read identically:
 *   - "Today" / "Yesterday" for the obvious cases.
 *   - Weekday name ("Monday", …, "Sunday") for the last 6 days.
 *   - Absolute "9th May 2026" for anything older — the 7-day cap kills the
 *     ambiguity of "Monday" meaning either 3 or 10 days ago.
 */
export function formatDayLabel(d: Date, t: TFunction, now: Date = new Date()): string {
    if (isToday(d)) return t('inbox.thread.day_divider.today', 'Today');
    if (isYesterday(d)) return t('inbox.thread.day_divider.yesterday', 'Yesterday');
    const daysAgo = differenceInCalendarDays(now, d);
    if (daysAgo > 0 && daysAgo < 7) return format(d, 'EEEE', { locale: dfLocale() });
    return format(d, 'do MMMM yyyy', { locale: dfLocale() });
}

/**
 * A rendered list entry: either a day-divider chip or a wrapped domain item.
 * Consumers switch on `kind` and read `label` (divider) or `value` (item).
 */
export type DayDividerItem<T> =
    | { readonly kind: 'divider'; readonly key: string; readonly label: string }
    | { readonly kind: 'item'; readonly key: string; readonly value: T };

/**
 * Interleave one day-divider (labelled via `formatDayLabel`) per local-day
 * boundary among a chronologically-ordered list. Shared by the message thread
 * and the notes timeline so both group by day identically.
 *
 * @param getDate maps an item to the Date whose local day drives grouping.
 * @param getKey  maps an item to its stable React key.
 */
export function buildDayDividerItems<T>(
    items: readonly T[],
    getDate: (item: T) => Date,
    getKey: (item: T) => string,
    t: TFunction,
    now: Date = new Date(),
): DayDividerItem<T>[] {
    const result: DayDividerItem<T>[] = [];
    let prevDateKey: string | null = null;
    for (const item of items) {
        const d = getDate(item);
        const dateKey = format(d, 'yyyy-MM-dd');
        if (dateKey !== prevDateKey) {
            result.push({
                kind: 'divider',
                key: `div-${dateKey}`,
                label: formatDayLabel(d, t, now),
            });
            prevDateKey = dateKey;
        }
        result.push({ kind: 'item', key: getKey(item), value: item });
    }
    return result;
}
